import React, { useState, useEffect, useCallback } from "react";
import { Typography, LinearProgress } from "@mui/material";
import PropTypes from "prop-types";
import {
  getQuestionnaire,
  submitQuestionnaire,
} from "../../../../services/v5Api";
import { useDispatch } from "react-redux";
import { setAPILoader } from "../../../../state/actions/globalAction";
import NoDataFound from "../../../common/NoDataFound";
import { useT } from "../../../../i18n/useT";
import { getCurrentLocale } from "../../../../utlis/locale";
import "./riskquestionnaire.scss";

// BCP-47 tags for the Web Speech API, keyed by app locale.
const SPEECH_LANG = {
  en: "en-US",
  ja: "ja-JP",
  zh: "zh-CN",
  es: "es-ES",
  fr: "fr-FR",
  ru: "ru-RU",
};

// Storage key prefix for localStorage
const STORAGE_KEY_PREFIX = "risk_questionnaire_";

const RiskQuestionnaire = ({ seniorId, onComplete, onClose }) => {
  const t = useT("riskInsights");
  // Questionnaire data state
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const [allQuestions, setAllQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  // Progress and navigation state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const startTimeRef = React.useRef(null);

  // UI state
  const [showSummary, setShowSummary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputMode, setInputMode] = useState("keyboard"); // "keyboard" or "voice"
  const [isListening, setIsListening] = useState(false);

  const dispatch = useDispatch();

  // Get storage key for this senior
  const getStorageKey = useCallback(
    (suffix) => `${STORAGE_KEY_PREFIX}${seniorId}_${suffix}`,
    [seniorId],
  );

  // Load questionnaire data
  useEffect(() => {
    const loadQuestionnaire = async () => {
      // setIsLoading(true); // Replaced by global loader
      dispatch(setAPILoader(true));
      setError(null);
      try {
        const response = await getQuestionnaire(seniorId);
        // API returns { status: 1, data: { ... } } or just data depending on service wrapper
        // Based on v5Api.js wrapper, it returns response.data directly.
        const data = response.data || response;

        if (!data || !data.sections) {
          throw new Error("Invalid questionnaire data format");
        }

        setQuestionnaireData(data);

        // Flatten all questions from all sections
        const questions = data.sections.flatMap((section) =>
          section.questions.map((q) => ({
            ...q,
            sectionId: section.sectionId,
            sectionTitle: section.sectionTitle,
            sectionType: section.sectionType,
          })),
        );
        setAllQuestions(questions);

        // Load saved progress from localStorage
        const savedResponses = localStorage.getItem(getStorageKey("responses"));
        const savedIndex = localStorage.getItem(getStorageKey("currentIndex"));
        const savedStartTime = localStorage.getItem(getStorageKey("startTime"));

        if (savedResponses) {
          setResponses(JSON.parse(savedResponses));
        }
        if (savedIndex) {
          setCurrentQuestionIndex(parseInt(savedIndex, 10));
        }
        if (savedStartTime) {
          startTimeRef.current = parseInt(savedStartTime, 10);
        } else {
          const now = Date.now();
          startTimeRef.current = now;
          localStorage.setItem(getStorageKey("startTime"), now.toString());
        }
      } catch (err) {
        console.error("Error loading questionnaire:", err);
        setError(t("load_failed"));
      } finally {
        // setIsLoading(false); // Replaced by global loader
        dispatch(setAPILoader(false));
        setIsLoading(false);
      }
    };

    loadQuestionnaire();
  }, [seniorId, getStorageKey]);

  // Save progress to localStorage whenever responses or index changes
  useEffect(() => {
    if (Object.keys(responses).length > 0) {
      localStorage.setItem(
        getStorageKey("responses"),
        JSON.stringify(responses),
      );
    }
    localStorage.setItem(
      getStorageKey("currentIndex"),
      currentQuestionIndex.toString(),
    );
  }, [responses, currentQuestionIndex, getStorageKey]);

  // Reset input mode to keyboard when question changes and it's a free text question
  useEffect(() => {
    const currentQuestion = allQuestions[currentQuestionIndex];
    if (currentQuestion?.questionType === "free_text") {
      setInputMode("keyboard");
      setIsListening(false);
    }
  }, [currentQuestionIndex, allQuestions]);

  // Handle single select option change
  const handleSingleSelectChange = (questionKey, questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionKey]: {
        questionId,
        value,
        answeredAt: new Date().toISOString(),
      },
    }));
  };

  // Handle multi select option change
  const handleMultiSelectChange = (questionKey, questionId, value) => {
    setResponses((prev) => {
      const currentValues = prev[questionKey]?.value || [];
      let newValues;

      // Handle "none" option - clear other selections
      if (value === "none") {
        newValues = currentValues.includes("none") ? [] : ["none"];
      } else {
        // Remove "none" if selecting other options
        const filteredValues = currentValues.filter((v) => v !== "none");
        newValues = filteredValues.includes(value)
          ? filteredValues.filter((v) => v !== value)
          : [...filteredValues, value];
      }

      return {
        ...prev,
        [questionKey]: {
          questionId,
          value: newValues,
          answeredAt: new Date().toISOString(),
        },
      };
    });
  };

  // Handle free text change
  const handleFreeTextChange = (questionKey, questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionKey]: {
        questionId,
        value,
        answeredAt: new Date().toISOString(),
      },
    }));
  };

  // Speech recognition for voice input
  const startVoiceInput = (questionKey, questionId) => {
    if (
      !("webkitSpeechRecognition" in window) &&
      !("SpeechRecognition" in window)
    ) {
      alert(t("voice_unsupported"));
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = SPEECH_LANG[getCurrentLocale()] || "en-US";

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      let finalTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        }
      }
      if (finalTranscript) {
        const currentValue = responses[questionKey]?.value || "";
        const newValue = currentValue
          ? currentValue + " " + finalTranscript
          : finalTranscript;
        handleFreeTextChange(questionKey, questionId, newValue);
      }
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();

    // Store recognition instance to stop it later
    window.currentRecognition = recognition;
  };

  const stopVoiceInput = () => {
    if (window.currentRecognition) {
      window.currentRecognition.stop();
      window.currentRecognition = null;
    }
    setIsListening(false);
  };

  // Handle number input change
  const handleNumberInputChange = (questionKey, questionId, value) => {
    // Allow empty string or valid numbers
    const numValue = value === "" ? "" : parseInt(value, 10);
    if (value !== "" && isNaN(numValue)) return;

    setResponses((prev) => ({
      ...prev,
      [questionKey]: {
        questionId,
        value: numValue,
        answeredAt: new Date().toISOString(),
      },
    }));
  };

  // Check if current question is answered
  const isCurrentQuestionAnswered = () => {
    const currentQuestion = allQuestions[currentQuestionIndex];
    if (!currentQuestion) return false;

    const response = responses[currentQuestion.questionKey];
    if (!response) return !currentQuestion.required;

    if (currentQuestion.questionType === "multi_select") {
      return response.value && response.value.length > 0;
    }
    if (currentQuestion.questionType === "free_text") {
      return (
        !currentQuestion.required ||
        (response.value && response.value.trim().length > 0)
      );
    }
    if (currentQuestion.questionType === "number_input") {
      const val = response.value;
      if (val === "" || val === undefined) return !currentQuestion.required;
      const min = currentQuestion.min || 0;
      const max = currentQuestion.max || 999;
      return val >= min && val <= max;
    }
    return response.value !== undefined && response.value !== "";
  };

  // Check if question should be shown (based on dependsOn)
  const shouldShowQuestion = (question) => {
    if (!question.dependsOn) return true;

    const dependentResponse = responses[question.dependsOn.questionKey];
    return dependentResponse?.value === question.dependsOn.value;
  };

  // Navigate to next question
  const handleNext = () => {
    if (currentQuestionIndex < allQuestions.length - 1) {
      let nextIndex = currentQuestionIndex + 1;

      // Skip questions that shouldn't be shown
      while (
        nextIndex < allQuestions.length &&
        !shouldShowQuestion(allQuestions[nextIndex])
      ) {
        nextIndex++;
      }

      if (nextIndex < allQuestions.length) {
        setCurrentQuestionIndex(nextIndex);
      } else {
        setShowSummary(true);
      }
    } else {
      setShowSummary(true);
    }
  };

  // Navigate to previous question
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      let prevIndex = currentQuestionIndex - 1;

      // Skip questions that shouldn't be shown
      while (prevIndex >= 0 && !shouldShowQuestion(allQuestions[prevIndex])) {
        prevIndex--;
      }

      if (prevIndex >= 0) {
        setCurrentQuestionIndex(prevIndex);
      }
    }
  };

  // Calculate progress
  const calculateProgress = () => {
    const answeredCount = allQuestions.filter((q) => {
      if (!shouldShowQuestion(q)) return true; // Skip hidden questions
      const response = responses[q.questionKey];
      if (q.questionType === "multi_select") {
        return response?.value?.length > 0;
      }
      if (q.questionType === "free_text") {
        return !q.required || response?.value?.trim().length > 0;
      }
      return response?.value !== undefined && response?.value !== "";
    }).length;

    return Math.round((answeredCount / allQuestions.length) * 100);
  };

  // Submit questionnaire
  const handleSubmit = async () => {
    // setIsSubmitting(true);
    dispatch(setAPILoader(true));
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // Create flat object for API submission
      const formattedResponses = {};
      Object.keys(responses).forEach((key) => {
        formattedResponses[key] = responses[key].value;
      });

      // Add timestamp as requested by user
      formattedResponses.createdAt = new Date().toISOString();

      // API call
      const response = await submitQuestionnaire(formattedResponses, seniorId);

      if (response && response.status === 1) {
        // Clear localStorage after successful submission
        localStorage.removeItem(getStorageKey("responses"));
        localStorage.removeItem(getStorageKey("currentIndex"));
        localStorage.removeItem(getStorageKey("startTime"));

        if (onComplete) {
          onComplete(response.data);
        }
      } else {
        throw new Error(response?.message || "Submission failed");
      }
    } catch (error) {
      console.error("Error submitting questionnaire:", error);
      setSubmitError(error.message || t("submit_failed"));
    } finally {
      // setIsSubmitting(false);
      dispatch(setAPILoader(false));
      setIsSubmitting(false);
    }
  };

  // Clear saved progress and start over
  const handleStartOver = () => {
    localStorage.removeItem(getStorageKey("responses"));
    localStorage.removeItem(getStorageKey("currentIndex"));
    localStorage.removeItem(getStorageKey("startTime"));
    setResponses({});
    setCurrentQuestionIndex(0);
    startTimeRef.current = Date.now();
    setShowSummary(false);
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="risk-questionnaire loading">
        {/* Global loader is active, so we just show the background */}
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="risk-questionnaire error">
        <NoDataFound
          icon="risk"
          message={t("load_failed_title")}
          description={error}
          actionLabel={t("close")}
          onAction={onClose}
        />
      </div>
    );
  }

  // Render summary/review page
  if (showSummary) {
    return (
      <div className="risk-questionnaire summary">
        <div className="questionnaire-header">
          <button className="close-btn" onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                fill="#212121"
              />
            </svg>
          </button>
          <div className="header-info">
            <Typography className="header-title">
              {t("review_answers_title")}
            </Typography>
          </div>
          <div style={{ width: "40px" }}></div>
        </div>

        <div className="summary-content">
          <div className="summary-stats">
            <Typography className="stats-text">
              {t("completed_percent", { percent: calculateProgress() })}
            </Typography>
          </div>

          <div className="summary-list">
            {allQuestions.map((question, index) => {
              if (!shouldShowQuestion(question)) return null;

              const response = responses[question.questionKey];
              let displayValue = "Not answered";

              if (response?.value !== undefined && response?.value !== "") {
                if (question.questionType === "multi_select") {
                  const selectedOptions = question.options.filter((opt) =>
                    response.value.includes(opt.value),
                  );
                  displayValue = selectedOptions
                    .map((opt) => opt.displayText)
                    .join(", ");
                } else if (question.questionType === "free_text") {
                  displayValue =
                    response.value.length > 100
                      ? response.value.substring(0, 100) + "..."
                      : response.value;
                } else if (question.questionType === "number_input") {
                  displayValue = response.value.toString();
                } else if (question.options) {
                  const selectedOption = question.options.find(
                    (opt) => opt.value === response.value,
                  );
                  displayValue = selectedOption?.displayText || response.value;
                } else {
                  displayValue = response.value.toString();
                }
              }

              return (
                <div
                  key={question.questionId}
                  className="summary-item"
                  onClick={() => {
                    setCurrentQuestionIndex(index);
                    setShowSummary(false);
                  }}
                >
                  <Typography className="summary-question">
                    {index + 1}. {question.questionText}
                  </Typography>
                  <Typography
                    className={`summary-answer ${!response?.value ? "not-answered" : ""}`}
                  >
                    {displayValue}
                  </Typography>
                </div>
              );
            })}
          </div>
        </div>

        <div className="questionnaire-actions">
          {submitError && (
            <div className="submit-error">
              <Typography color="error">{submitError}</Typography>
            </div>
          )}
          <button
            className="btn btn-outline"
            onClick={() => setShowSummary(false)}
            disabled={isSubmitting}
          >
            {t("edit_answers")}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t("submitting") : t("submit_assessment")}
          </button>
        </div>
      </div>
    );
  }

  // Get current question
  const currentQuestion = allQuestions[currentQuestionIndex];
  if (!currentQuestion) return null;

  // Skip to next visible question if current should be hidden
  if (!shouldShowQuestion(currentQuestion)) {
    handleNext();
    return null;
  }

  const progress = ((currentQuestionIndex + 1) / allQuestions.length) * 100;
  const isLastQuestion = currentQuestionIndex === allQuestions.length - 1;

  return (
    <div className="risk-questionnaire">
      {/* Header */}
      <div className="questionnaire-header">
        <button className="close-btn" onClick={onClose}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
              fill="#212121"
            />
          </svg>
        </button>
        <div className="header-info">
          <Typography className="header-title">
            {questionnaireData?.title}
          </Typography>
          <Typography className="header-subtitle">
            {t("question_of", {
              current: currentQuestionIndex + 1,
              total: allQuestions.length,
            })}
          </Typography>
        </div>
        <button className="menu-btn" onClick={handleStartOver}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z"
              fill="#666"
            />
          </svg>
        </button>
      </div>

      {/* Progress Bar */}
      <div className="progress-container">
        <LinearProgress
          variant="determinate"
          value={progress}
          className="progress-bar"
        />
        <Typography className="progress-text">
          {t("percent_complete", { percent: Math.round(progress) })}
        </Typography>
      </div>

      {/* Question Content */}
      <div className="question-content">
        <Typography className="question-text">
          {currentQuestion.questionText}
          {currentQuestion.required && <span className="required">*</span>}
        </Typography>

        {/* Multiple Choice Options */}
        {(currentQuestion.questionType === "single_select" ||
          currentQuestion.questionType === "multi_select") && (
          <div className="options-container">
            {currentQuestion.options.map((option) => {
              const isSelected =
                currentQuestion.questionType === "multi_select"
                  ? responses[currentQuestion.questionKey]?.value?.includes(
                      option.value,
                    )
                  : responses[currentQuestion.questionKey]?.value ===
                    option.value;

              return (
                <button
                  key={option.optionId}
                  className={`option-btn ${isSelected ? "selected" : ""}`}
                  onClick={() =>
                    currentQuestion.questionType === "multi_select"
                      ? handleMultiSelectChange(
                          currentQuestion.questionKey,
                          currentQuestion.questionId,
                          option.value,
                        )
                      : handleSingleSelectChange(
                          currentQuestion.questionKey,
                          currentQuestion.questionId,
                          option.value,
                        )
                  }
                >
                  <span
                    className={`option-indicator ${currentQuestion.questionType === "multi_select" ? "checkbox" : "radio"}`}
                  >
                    {isSelected && (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
                          fill="white"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="option-text">{option.displayText}</span>
                </button>
              );
            })}
            {currentQuestion.questionType === "multi_select" && (
              <Typography className="multi-select-hint">
                {t("select_all")}
              </Typography>
            )}
          </div>
        )}

        {/* Free Text Input */}
        {currentQuestion.questionType === "free_text" && (
          <div className="freetext-container">
            <div className="freetext-input-wrapper">
              <textarea
                className={`freetext-input ${isListening ? "listening" : ""}`}
                value={responses[currentQuestion.questionKey]?.value || ""}
                onChange={(e) =>
                  handleFreeTextChange(
                    currentQuestion.questionKey,
                    currentQuestion.questionId,
                    e.target.value,
                  )
                }
                placeholder={t("freetext_placeholder")}
                maxLength={currentQuestion.maxLength}
                disabled={inputMode === "voice" && isListening}
              />
              {isListening && (
                <div className="listening-indicator">
                  <span className="pulse-dot"></span>
                  <Typography className="listening-text">
                    {t("listening")}
                  </Typography>
                </div>
              )}
            </div>

            <div className="freetext-footer">
              <Typography className="char-count">
                {responses[currentQuestion.questionKey]?.value?.length || 0} /{" "}
                {currentQuestion.maxLength}
              </Typography>

              {/* Input Mode Toggle */}
              <div className="input-mode-toggle">
                <button
                  className={`toggle-btn ${inputMode === "keyboard" ? "active" : ""}`}
                  onClick={() => {
                    if (isListening) stopVoiceInput();
                    setInputMode("keyboard");
                  }}
                  title={t("type_response")}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 5H4C2.9 5 2 5.9 2 7V17C2 18.1 2.9 19 4 19H20C21.1 19 22 18.1 22 17V7C22 5.9 21.1 5 20 5ZM20 17H4V7H20V17ZM11 8H13V10H11V8ZM11 11H13V13H11V11ZM8 8H10V10H8V8ZM8 11H10V13H8V11ZM5 11H7V13H5V11ZM5 8H7V10H5V8ZM8 14H16V16H8V14ZM14 11H16V13H14V11ZM14 8H16V10H14V8ZM17 11H19V13H17V11ZM17 8H19V10H17V8Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                <button
                  className={`toggle-btn ${inputMode === "voice" ? "active" : ""} ${isListening ? "recording" : ""}`}
                  onClick={() => {
                    setInputMode("voice");
                    if (!isListening) {
                      startVoiceInput(
                        currentQuestion.questionKey,
                        currentQuestion.questionId,
                      );
                    } else {
                      stopVoiceInput();
                    }
                  }}
                  title={
                    isListening ? t("stop_recording") : t("speak_response")
                  }
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14ZM17.3 11C17.3 14 14.76 16.1 12 16.1C9.24 16.1 6.7 14 6.7 14H5C5 14.41 7.72 17.23 11 17.72V21H13V17.72C16.28 17.23 19 14.41 19 11H17.3Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Number Input */}
        {currentQuestion.questionType === "number_input" && (
          <div className="number-input-container">
            <input
              type="number"
              className="number-input"
              value={responses[currentQuestion.questionKey]?.value ?? ""}
              onChange={(e) =>
                handleNumberInputChange(
                  currentQuestion.questionKey,
                  currentQuestion.questionId,
                  e.target.value,
                )
              }
              placeholder={currentQuestion.placeholder || t("enter_number")}
              min={currentQuestion.min}
              max={currentQuestion.max}
            />
            {currentQuestion.min !== undefined &&
              currentQuestion.max !== undefined && (
                <Typography className="number-hint">
                  {t("value_between", {
                    min: currentQuestion.min,
                    max: currentQuestion.max,
                  })}
                </Typography>
              )}
          </div>
        )}
      </div>

      {/* Navigation Actions */}
      <div className="questionnaire-actions">
        <button
          className="btn btn-outline"
          onClick={handlePrevious}
          disabled={
            currentQuestionIndex === 0 ||
            (currentQuestion.questionType === "free_text" &&
              inputMode === "voice" &&
              isListening)
          }
        >
          {t("previous")}
        </button>
        <button
          className="btn btn-primary"
          onClick={handleNext}
          disabled={
            (!isCurrentQuestionAnswered() && currentQuestion.required) ||
            (currentQuestion.questionType === "free_text" &&
              inputMode === "voice" &&
              isListening)
          }
        >
          {isLastQuestion ? t("review_answers") : t("next")}
        </button>
      </div>
    </div>
  );
};

RiskQuestionnaire.propTypes = {
  seniorId: PropTypes.string.isRequired,
  onComplete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

RiskQuestionnaire.defaultProps = {
  onComplete: () => {},
};

export default RiskQuestionnaire;
